/**
 * THE FLAG'S TWO BAR METERS — the reference widget's connected state, ported.
 *
 * The reference (voice2me `AudioVisualizer` + `WavRenderer.drawBars`) draws two
 * independent canvas meters side by side while a call runs: AGENT ON THE LEFT,
 * USER ON THE RIGHT, both in the flag's own ink colour, ten flat rectangular
 * bars each, growing symmetrically from the vertical centre with a 0.1 floor —
 * so silence reads as a thin dotted line rather than an empty box. This file
 * reproduces that object. The numbers that matter and where they come from:
 *
 *   - 10 BARS. The reference computes `floor(canvasWidth / (barWidth + gap))`
 *     against an (accidentally) fixed 300-wide bitmap with barWidth 18 and
 *     gap 10 — ten bars. The accident is not ported; the ten bars are.
 *   - GEOMETRY BY RATIO. Bar:gap = 18:10 of a 300-unit width, scaled to the
 *     real box — at the flag's ~50×32 meters that is ≈3 px bars with ≈1.7 px
 *     gaps, which is exactly what the reference's stretched bitmap displayed.
 *   - SQUARE CORNERS, ONE FLAT COLOUR, `fillRect`. No gradient, no radius.
 *   - FLOOR 0.1, CENTRE GROWTH, minimum 1 px so the floor never vanishes.
 *   - A SELF-CHAINING requestAnimationFrame gated on the call being live; when
 *     it is not, ONE frame is drawn (the floor line) and the loop stops.
 *
 * WHAT DRIVES THE HEIGHTS, and how the ten bands are cut. The reference runs an
 * 8192-point FFT at 24 kHz, buckets 4096 bins into 72 semitone bands
 * (32–2000 Hz, max dB per band, −100..−30 dB window) and peak-downsamples to
 * ten bars — LOGARITHMIC band edges, ascending pitch left to right. Our meter
 * exposes a 256-point transform (`meter.ts` SPECTRUM_FFT_SIZE, byte values over
 * the same default −100..−30 dB window), and the first cut of this file mapped
 * the bars onto ten ADJACENT bins of it. That looked broken in a specific way:
 * ten neighbouring ~190 Hz bins sit under the same stretch of the formant
 * envelope, so they carry nearly the same energy and every bar rode the same
 * height. The bands are therefore log-spaced like the reference's, from the
 * first bin above DC up to a third of the spectrum (≈8 kHz at a 48 kHz
 * context, expressed in BIN COUNTS so no sample rate needs guessing), max per
 * band: the low bars ride the fundamental and low formants, the high bars ride
 * fricatives and consonant bursts, and speech's natural spectral tilt is what
 * makes them DIFFER — which is the entire point of a ten-bar meter.
 *
 * ONE SHAPING CURVE ON TOP: `v^1.6`. The shared spectrum analyser smooths at
 * 0.85 (chosen by the conversation bar's port, see meter.ts — this file may
 * not retune a shared node), which squeezes byte values toward the middle of
 * the range; a mild power curve re-deepens the valleys the smoothing filled
 * in. The reference needed no such curve because its analyser smoothed at 0.1.
 *
 * `spectrum` is OPTIONAL on `VoiceControl` (a hand-made control in a test does
 * not fake an FFT), and the fallback mirrors the reference's own degenerate
 * branch: with only a scalar to show, every bar renders the same
 * `max(level, floor)` — the reference gets there by interpolating a
 * one-element array, this gets there by writing the level into all ten.
 *
 * Inline styles and zero stylesheet dependence, for the flag's own reason (see
 * flag-button.tsx's header): this renders inside a closed shadow root on
 * somebody else's page, and a brand object may not fall apart when a stylesheet
 * does.
 */
import { useEffect, useRef } from 'react'
import type { VoiceControl } from './types'

/** The reference bitmap's proportions: bar 18, gap 10, of a 300-unit width. */
const UNIT_W = 300
const UNIT_BAR = 18
const UNIT_GAP = 10
const BARS = 10
/** The amplitude floor — the reference's `minValue`. */
const FLOOR = 0.1
/** Bins above DC that feed the bars. Bin 0 of a 256-point transform holds DC
 *  and mains rumble, which would pin the first bar; speech starts above it. */
const FIRST_BIN = 1
/** The top band edge, as a fraction of the bin count: a third of the spectrum
 *  is ≈8 kHz at a 48 kHz context — past that is hiss, not speech. */
const TOP_BIN_FRACTION = 1 / 3
/** Contrast: re-deepens what the shared analyser's 0.85 smoothing flattened. */
const GAMMA = 1.6
/** What fraction of the box the reference's ten bars actually cover — a leading
 *  gap plus ten bar-and-gap pairs, out of 300. Held constant across bar counts so
 *  a five-bar meter is the same object with fewer bars in it. See the draw loop. */
const FILL = (UNIT_GAP + BARS * (UNIT_BAR + UNIT_GAP)) / UNIT_W

export interface FlagBarsProps {
  control: VoiceControl
  /** Keeps the frame loop chaining. False draws one floor-line frame and stops. */
  active: boolean
  /** The ink — the reference drew #000 on its yellow; here it is `onAccent`. */
  color: string
  /** One meter's box. Two of these render side by side. */
  meterWidth?: number
  meterHeight?: number
  gap?: number
  /**
   * HOW MANY BARS PER METER. Ten — the flag reference's own count — by default.
   *
   * A COUNT RATHER THAN A PIXEL WIDTH, and that is a correction. The first version
   * of this prop took `barWidth`/`barGap` in pixels and derived the count from the
   * box, which is how the voice2me visualiser is written; the trouble is that the
   * count is then a function of the meter's width, so a customer who widened the
   * button silently got a different number of bars. The count is the thing anybody
   * actually has an opinion about, so it is the thing to ask for — the widths come
   * off the 18:10 ratio below, scaled to fill the same proportion of the box that
   * ten bars fill. At `bars = 10` the arithmetic collapses to exactly what it was.
   */
  bars?: number
  /** Agent left, user right is the flag reference's order and the default. The
   *  voice2me pill draws the user first, so it asks for the swap. */
  reverse?: boolean
}

export function FlagBars({
  control,
  active,
  color,
  meterWidth = 50,
  meterHeight = 32,
  gap = 4,
  bars = BARS,
  reverse = false,
}: FlagBarsProps) {
  // Agent on the left, user on the right — the reference's order.
  const agentRef = useRef<HTMLCanvasElement>(null)
  const userRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvases: Array<[HTMLCanvasElement | null, 'assistant' | 'user']> = [
      [agentRef.current, 'assistant'],
      [userRef.current, 'user'],
    ]
    // Caller-supplied buffer, allocated once — spectrum() writes into it and
    // returns how many bins are real. 512 comfortably holds a 256-point
    // transform's 128 bins.
    const bins = new Uint8Array(512)
    const values = new Float32Array(bars)
    let frame = 0

    const drawOne = (canvas: HTMLCanvasElement | null, track: 'assistant' | 'user') => {
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      // Sized every frame against the box and the device, so a zoom or a DPI
      // change never leaves a stretched bitmap — the reference's one bug here.
      const dpr = window.devicePixelRatio || 1
      const w = meterWidth
      const h = meterHeight
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr
        canvas.height = h * dpr
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      const got = control.spectrum?.(track, bins) ?? 0
      if (got > FIRST_BIN) {
        // Log-spaced band edges from FIRST_BIN to the top, in bin units. Low
        // bands are one bin wide; high bands aggregate several (max per band,
        // the reference's own downsampling rule).
        const top = Math.max(FIRST_BIN + bars, Math.floor(got * TOP_BIN_FRACTION))
        const ratio = top / FIRST_BIN
        for (let i = 0; i < bars; i++) {
          const from = Math.round(FIRST_BIN * Math.pow(ratio, i / bars))
          const until = Math.max(from + 1, Math.round(FIRST_BIN * Math.pow(ratio, (i + 1) / bars)))
          let peak = 0
          for (let b = from; b < until && b < got; b++) {
            const v = bins[b] ?? 0
            if (v > peak) peak = v
          }
          values[i] = Math.pow(peak / 255, GAMMA)
        }
      } else {
        // No FFT on this control (or nothing readable yet): the scalar level,
        // in every bar — the reference's own fallback picture.
        const level = track === 'user' ? control.levels().user : control.levels().assistant
        values.fill(level)
      }

      /**
       * The 18:10 ratio, scaled so any bar count fills the box the way ten do.
       *
       * Ten bars at the reference's proportions consume `10 + 10 * 28 = 290` of a
       * 300-unit width — a leading gap and then the bars, with one gap of slack at
       * the far end. `FILL` is that 290/300, and holding it constant is what makes
       * five bars look like the same meter rather than like ten bars with half of
       * them missing. Substituting `bars = BARS` reduces this to `w / UNIT_W`,
       * which is what it was before the count became a prop.
       */
      const s = (w * FILL) / (UNIT_GAP + bars * (UNIT_BAR + UNIT_GAP))
      const barW = UNIT_BAR * s
      const gapW = UNIT_GAP * s
      ctx.fillStyle = color
      for (let i = 0; i < bars; i++) {
        const v = Math.max(values[i] ?? 0, FLOOR)
        const height = Math.max(1, v * h)
        const x = gapW + i * (barW + gapW)
        ctx.fillRect(x, (h - height) / 2, barW, height)
      }
    }

    const render = () => {
      drawOne(canvases[0]![0], canvases[0]![1])
      drawOne(canvases[1]![0], canvases[1]![1])
      if (active) frame = window.requestAnimationFrame(render)
    }
    render()
    return () => window.cancelAnimationFrame(frame)
  }, [control, active, color, meterWidth, meterHeight, bars])

  const box = { width: meterWidth, height: meterHeight, display: 'block' } as const
  return (
    <span
      aria-hidden
      style={{ display: 'flex', alignItems: 'center', gap, flexDirection: reverse ? 'row-reverse' : 'row' }}
    >
      <canvas ref={agentRef} style={box} />
      <canvas ref={userRef} style={box} />
    </span>
  )
}
